import { Ban, Briefcase, User } from "lucide-react";
import { useGetList, useNotify, useUpdate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { formatCents } from "@/lib/money";
import { cn } from "@/lib/utils";

import { useTeammates } from "../comments/useTeammates";
import { fullName } from "../comments/mentions";
import { StatusBadge } from "../data-display/StatusBadge";

const inputClass =
  "h-8 w-full rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

interface Deal {
  id: number;
  name: string;
  stage: string;
  amount: number | null;
}
interface Job {
  id: string;
  title: string;
  status: string;
}
interface Estimate {
  id: string;
  estimate_number: number;
  status: string;
  total_cents: number;
}
interface Invoice {
  id: string;
  invoice_number: number;
  status: string;
  balance_cents: number;
}

/**
 * Who this conversation is with, and what is going on with them.
 *
 * Answering a customer without their open deal, next job and unpaid invoice in
 * view is how staff end up asking questions the CRM already knows the answer
 * to. Everything here is one click from the reply box.
 *
 * `deals.amount` is in DOLLARS (Atomic CRM heritage) while invoices are in
 * CENTS, the values are formatted accordingly rather than through one helper.
 */
export const ConversationContext = ({
  contactId,
  contactName,
  assignedTo,
  conversationId,
  doNotContact,
  className,
}: {
  contactId: number;
  contactName: string;
  assignedTo: number | null;
  conversationId: string;
  doNotContact?: boolean | null;
  className?: string;
}) => {
  const notify = useNotify();
  const [update] = useUpdate();
  const queryClient = useQueryClient();
  const { data: team } = useTeammates();

  const { data: deals } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "updated_at", order: "DESC" },
    filter: { "contact_ids@cs": `{${contactId}}`, "archived_at@is": null },
  });
  const { data: jobs } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "created_at", order: "DESC" },
    filter: { contact_id: contactId },
  });
  const { data: estimates } = useGetList<Estimate>("estimates", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "created_at", order: "DESC" },
    filter: { contact_id: contactId },
  });
  const { data: invoices } = useGetList<Invoice>("invoices", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "created_at", order: "DESC" },
    filter: { contact_id: contactId },
  });

  const assign = (value: string) => {
    update(
      "conversations",
      {
        id: conversationId,
        data: { assigned_to: value ? Number(value) : null },
        previousData: { id: conversationId, assigned_to: assignedTo },
      },
      {
        mutationMode: "pessimistic",
        // The inbox list is a react-query hook with its own key, so a ra-core
        // mutation does not invalidate it. Without this the assignment saves
        // but the dropdown snaps back until a manual reload, it looked like
        // the write had failed.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["inbox"] }),
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not assign", {
            type: "error",
          }),
      },
    );
  };

  const openInvoices = (invoices ?? []).filter((i) => i.balance_cents > 0);

  return (
    <div className={cn("flex min-w-0 flex-col gap-3", className)}>
      <div>
        <Link
          to={`/contacts/${contactId}/show`}
          className="flex items-center gap-1.5 text-[13.5px] font-medium text-foreground no-underline hover:underline"
        >
          <User className="size-3.5 text-muted-foreground" />
          {contactName}
        </Link>
        {doNotContact ? (
          <p className="mt-1 flex items-center gap-1 text-[12px] text-amber-700">
            <Ban className="size-3" />
            Do not contact, outbound is blocked
          </p>
        ) : null}
      </div>

      <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        Assigned to
        <select
          className={inputClass}
          value={assignedTo ?? ""}
          onChange={(event) => assign(event.target.value)}
        >
          <option value="">Unassigned</option>
          {(team ?? []).map((person) => (
            <option key={person.id} value={person.id}>
              {fullName(person)}
            </option>
          ))}
        </select>
      </label>

      {(deals ?? []).length ? (
        <Section title="Open deals">
          {(deals ?? []).map((deal) => (
            <Row
              key={deal.id}
              to={`/deals/${deal.id}/show`}
              label={deal.name}
              // Dollars, not cents, see the note above.
              meta={
                deal.amount != null ? `$${deal.amount.toLocaleString()}` : ""
              }
            />
          ))}
        </Section>
      ) : null}

      {(jobs ?? []).length ? (
        <Section title="Jobs">
          {(jobs ?? []).map((job) => (
            <Row
              key={job.id}
              to={`/jobs/${job.id}/show`}
              label={job.title}
              badge={job.status.replace(/_/g, " ")}
            />
          ))}
        </Section>
      ) : null}

      {(estimates ?? []).length ? (
        <Section title="Estimates">
          {(estimates ?? []).map((estimate) => (
            <Row
              key={estimate.id}
              to={`/estimates/${estimate.id}/show`}
              label={`#${estimate.estimate_number}`}
              meta={formatCents(estimate.total_cents)}
              badge={estimate.status}
            />
          ))}
        </Section>
      ) : null}

      {openInvoices.length ? (
        <Section title="Unpaid invoices">
          {openInvoices.map((invoice) => (
            <Row
              key={invoice.id}
              to={`/invoices/${invoice.id}/show`}
              label={`#${invoice.invoice_number}`}
              meta={`${formatCents(invoice.balance_cents)} due`}
              badge={invoice.status.replace(/_/g, " ")}
            />
          ))}
        </Section>
      ) : null}

      {!(deals ?? []).length &&
      !(jobs ?? []).length &&
      !(estimates ?? []).length &&
      !openInvoices.length ? (
        <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <Briefcase className="size-3.5" />
          No open work for this customer.
        </p>
      ) : null}
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="min-w-0">
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
      {title}
    </p>
    <ul className="flex flex-col gap-1">{children}</ul>
  </div>
);

const Row = ({
  to,
  label,
  meta,
  badge,
}: {
  to: string;
  label: string;
  meta?: string;
  badge?: string;
}) => (
  <li className="flex min-w-0 items-center justify-between gap-2">
    <Link
      to={to}
      className="min-w-0 truncate text-[12.5px] text-foreground no-underline hover:underline"
    >
      {label}
    </Link>
    <span className="flex shrink-0 items-center gap-1.5">
      {meta ? (
        <span className="text-[12px] tabular-nums text-muted-foreground">
          {meta}
        </span>
      ) : null}
      {badge ? <StatusBadge label={badge} tone="neutral" /> : null}
    </span>
  </li>
);
